'use client';

import {
  Autocomplete,
  Box,
  CloseButton,
  Flex,
  Grid,
  Group,
  Kbd,
  ThemeIcon,
} from '@mantine/core';
import { forwardRef, useEffect, useState } from 'react';
import { getHints, searchWord } from './hook';

import DMText from '@/components/DMText';
import { IconSearch } from '@tabler/icons-react';
import { isDark } from '@/utils/utils';
import { searchKeyword } from '@/features/Search/hook/search.state';
import themeVariables from '@/styles/themeVariables';
import { useHydrateAtoms } from 'jotai/utils';
import { useScrollInit } from '@/utils/hook';
import { useSearchPageStyle } from './page.style';
import { useRouter } from 'next/navigation';

const SearchPage = ({ keyword }) => {
  const router = useRouter();
  useHydrateAtoms([[searchKeyword, keyword]]);

  useEffect(() => {
    useScrollInit();
    router.replace('/');
  }, []);

  return (
    <>
      <Grid columns={24}>
        <Grid.Col></Grid.Col>
      </Grid>
    </>
  );
};

export default SearchPage;

const SearchInput = () => {
  const { dark } = isDark();
  const { classes } = useSearchPageStyle({ dark });

  const { hints, setHintSearch } = getHints();
  const { ref, focused, inputRef, keyword, handleKeywordChange } = searchWord();

  const [inputValue, setInputValue] = useState(() => keyword);

  return (
    <Group position={'center'}>
      <Box
        ref={ref}
        sx={() => ({
          width: '100%',
          maxWidth: 720,
          transition: '0.3s all ease',
          transform: focused ? 'translateY(-4px)' : 'translateY(0px)',
        })}
      >
        <Autocomplete
          ref={inputRef}
          iconWidth={70}
          w={'100%'}
          value={inputValue}
          onChange={(value) => {
            setInputValue(value);
            setHintSearch(value);
          }}
          placeholder={'검색 키워드를 입력해주세요'}
          classNames={{
            input: classes.searchInput,
            rightSection: classes.searchInputRightSection,
          }}
          rightSection={
            <Group>
              {inputRef?.current?.value ? (
                <CloseButton
                  radius={'xl'}
                  aria-label={'remove button'}
                  onClick={() => {
                    setHintSearch('');
                    setInputValue('');
                    inputRef.current?.focus();
                  }}
                  sx={{
                    color: themeVariables.darkMode.normal(dark),
                  }}
                />
              ) : (
                <Flex w={22} />
              )}
              <Flex
                mr={10}
                align={'center'}
                className={classes.searchInputKbd}
                onClick={handleKeywordChange}
              >
                <Kbd px={6}>Enter ↳</Kbd>
              </Flex>
            </Group>
          }
          icon={
            <IconSearch
              stroke={2.5}
              color={themeVariables.darkMode.neutral(dark)}
            />
          }
          itemComponent={forwardRef(AutoCompleteItem)}
          onItemSubmit={handleKeywordChange}
          data={hints?.keywords || []}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleKeywordChange('');
            }
          }}
          limit={6}
          withinPortal
          sx={{
            '::placeholder': {
              color: themeVariables.darkMode.alternative(dark),
            },
            color: themeVariables.darkMode.normal(dark),
          }}
        />
      </Box>
    </Group>
  );
};

const AutoCompleteItem = (
  { value, ...others }: any,
  ref: React.Ref<HTMLInputElement>
) => {
  return (
    <div ref={ref} {...others}>
      <Group noWrap px={12}>
        <ThemeIcon radius={'xl'} bg={themeVariables.colors.light100}>
          <IconSearch
            size={13}
            stroke={2.5}
            color={themeVariables.fontColors.light}
          />
        </ThemeIcon>
        <DMText size={15} weight={500} dColor={themeVariables.darkMode.normal}>
          {value}
        </DMText>
      </Group>
    </div>
  );
};
